<template>
  <ButtonMenu
    ref="buttonMenuEl"
    class="embed-console"
    is-hidden-on-click
    placement="bottom-start"
    @is-shown="onShow"
  >
    <template #btn>
      <slot name="trigger" />
    </template>

    <template #content>
      <ParsedInput
        ref="parsedInputEl"
        class="parsed-input"
        :style="`min-width: ${minWidth ? minWidth : '350px'};`"
        placeholder="Type..."
        compact
        :no-completion-helpers="noCompletionHelpers"
        @blur="$emit('blur')"
        @close="close"
        @submit="submit"
        @validate-custom-completion="
          (pl) => $emit('validateCustomCompletion', pl)
        "
      >
        <template #completion-helpers="props">
          <slot name="completion-helpers" :completion="props.completion" />
        </template>
        <template #customCompletions="props">
          <slot
            v-if="props"
            name="customCompletions"
            :mouse-over="props.mouseOver"
            :selected="props.selected"
          />
        </template>
      </ParsedInput>
    </template>
  </ButtonMenu>
</template>

<script lang="ts">
import { defineComponent, nextTick, PropType, ref, toRefs, watch } from "vue";

import { ConsoleParsingMode } from "@ctrl/controller-rpc";

import ButtonMenu from "@/components/elements/ButtonMenu.vue";
import { State } from "@/components/parsed-input";
import ParsedInput, {
  Query,
  UpdateObjectQuery,
} from "@/components/ParsedInput.vue";

export default defineComponent({
  components: {
    ButtonMenu,
    ParsedInput,
  },
  props: {
    mode: {
      type: String as PropType<ConsoleParsingMode>,
      required: true,
    },
    minWidth: {
      type: String,
      default: null,
    },
    noCompletionHelpers: {
      type: Boolean,
      default: false,
    },
  },
  emits: {
    submit: (_: UpdateObjectQuery) => true,
    blur: () => true,
    validateCustomCompletion: (_: number) => true,
  },
  setup(props, { emit }) {
    const { mode } = toRefs(props);
    const parsedInputEl = ref<InstanceType<typeof ParsedInput>>();
    const buttonMenuEl = ref<InstanceType<typeof ButtonMenu>>();
    const close = () => {
      buttonMenuEl.value?.hide();
      nextTick(() => parsedInputEl.value?.clear(true));
    };
    watch(
      () => !!parsedInputEl.value?.isFocused,
      (isFocused) => {
        if (!isFocused) close();
      }
    );
    const onShow = (isShown: boolean) => {
      if (isShown) {
        const state: State = {
          mode: mode.value,
        };
        parsedInputEl.value?.fill(state);
        parsedInputEl.value?.focus();
      }
    };
    const submit = (value: Query) => {
      if (value.kind === "update") {
        emit("submit", value);
      }
      close();
    };
    const show = () => {
      buttonMenuEl.value?.show();
    };
    return {
      buttonMenuEl,
      close,
      onShow,
      parsedInputEl,
      show,
      submit,
    };
  },
});
</script>

<style lang="scss" scoped>
.parsed-input {
  & :deep(.input) .query:not(.locked) {
    padding: 6px 6px 12px;
    @extend %font-body-regular;
  }

  & :deep(.completions) {
    .completion .helper .helper-text {
      @extend %font-small-regular;
    }
  }
}
</style>
